import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS
} from '../../../utils/inputValidation';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Building,
  Tag,
  Star,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  MessageSquare,
  ShoppingCart,
  Calendar,
  Download,
  Edit,
  Plus,
  Filter,
  MoreVertical,
  ExternalLink,
  Eye,
  Send,
  PhoneCall,
  MessageCircle,
  Mail as MailIcon,
  FileImage,
  Award,
  TrendingUp,
  Package,
  Truck,
  Shield,
  History,
  Settings,
  X,
  ArrowLeft,
  IndianRupee,
  Database,
  HardDrive
} from "lucide-react";
import Button from '../../../components/common/Button/Button';
import customerService from '../../../api/crm/customers/customerService';
import PermissionGate from '../../../components/common/PermissionGate';
import orderService from '../../../api/crm/orders/orderService';
import quotationService from '../../../api/crm/quotations/quotationService';
import { formatDate } from '../../../utils/stringUtils';
import { getQuotationStatusClasses, formatQuotationStatus } from '../../../utils/quotationStatusUtils';
import { formatIndianAmount } from '../../../utils/amountFormatter';

// Helper to format address object into readable string
const formatAddress = (addressObj) => {
  if (!addressObj) return '';

  // If it's already a string, check if it's a stringified object
  if (typeof addressObj === 'string') {
    const trimmed = addressObj.trim();
    // If it looks like a stringified object/array, try to parse it
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(trimmed);
        // Recursively call formatAddress with parsed object
        return formatAddress(parsed);
      } catch (e) {
        // If JSON parsing fails, try to extract values using regex
        // Handle format like: "{ street: '', city: '', state: '', pincode: '', country: 'India' }"
        const streetMatch = trimmed.match(/street:\s*['"]([^'"]*)['"]/);
        const cityMatch = trimmed.match(/city:\s*['"]([^'"]*)['"]/);
        const stateMatch = trimmed.match(/state:\s*['"]([^'"]*)['"]/);
        const pincodeMatch = trimmed.match(/pincode:\s*['"]([^'"]*)['"]/);
        const countryMatch = trimmed.match(/country:\s*['"]([^'"]*)['"]/);

        const parts = [];
        if (streetMatch && streetMatch[1] && streetMatch[1].trim()) parts.push(streetMatch[1].trim());
        if (cityMatch && cityMatch[1] && cityMatch[1].trim()) parts.push(cityMatch[1].trim());
        if (stateMatch && stateMatch[1] && stateMatch[1].trim()) parts.push(stateMatch[1].trim());
        if (pincodeMatch && pincodeMatch[1] && pincodeMatch[1].trim()) parts.push(pincodeMatch[1].trim());
        if (countryMatch && countryMatch[1] && countryMatch[1].trim() && countryMatch[1].trim() !== 'India') {
          parts.push(countryMatch[1].trim());
        }

        return parts.length > 0 ? parts.join(', ') : '';
      }
    }
    // If it's a normal string, return it
    return trimmed || '';
  }

  // If it's an object, build formatted string
  if (typeof addressObj === 'object' && addressObj !== null) {
    // Check if it's an array (shouldn't be, but handle it)
    if (Array.isArray(addressObj)) return '';

    const parts = [];
    // Only add non-empty string values, and check for stringified objects
    if (addressObj.street && typeof addressObj.street === 'string') {
      const street = addressObj.street.trim();
      if (street.startsWith('{') || street.startsWith('[')) {
        const parsedStreet = formatAddress(street);
        if (parsedStreet) parts.push(parsedStreet);
      } else if (street) {
        parts.push(street);
      }
    }
    if (addressObj.street2 && typeof addressObj.street2 === 'string' && addressObj.street2.trim()) {
      parts.push(addressObj.street2.trim());
    }
    if (addressObj.street3 && typeof addressObj.street3 === 'string' && addressObj.street3.trim()) {
      parts.push(addressObj.street3.trim());
    }
    if (addressObj.city && typeof addressObj.city === 'string' && addressObj.city.trim() && !addressObj.city.trim().startsWith('{')) {
      parts.push(addressObj.city.trim());
    }
    if (addressObj.state && typeof addressObj.state === 'string' && addressObj.state.trim() && !addressObj.state.trim().startsWith('{')) {
      parts.push(addressObj.state.trim());
    }
    if (addressObj.pincode && typeof addressObj.pincode === 'string' && addressObj.pincode.trim() && !addressObj.pincode.trim().startsWith('{')) {
      parts.push(addressObj.pincode.trim());
    }
    // Only add country if it's not India (to avoid redundancy)
    if (addressObj.country && typeof addressObj.country === 'string' && addressObj.country.trim() && addressObj.country.trim() !== 'India' && !addressObj.country.trim().startsWith('{')) {
      parts.push(addressObj.country.trim());
    }

    // Return formatted string if we have any parts, otherwise empty
    return parts.length > 0 ? parts.join(', ') : '';
  }

  return '';
};

// Helper function to transform customer 360 data
// Only transforms customer profile - orders, tickets, metrics, and interactions are fetched via separate APIs
const transformCustomer360Data = (result, id) => {
  if (!result.success || !result.data) {
    return {
      customer: null,
      error: result.error || 'Failed to load customer 360 data'
    };
  }

  const apiCustomer = result.data.customer || null;

  // Map backend customer shape into UI-friendly customer object
  const mappedCustomer = apiCustomer
    ? {
      id: apiCustomer._id || apiCustomer.customerId || id,
      name:
        apiCustomer.contactPerson ||
        apiCustomer.companyName ||
        apiCustomer.name ||
        "N/A",
      businessName: apiCustomer.companyName || "",
      email: apiCustomer.email || "",
      phone: Array.isArray(apiCustomer.phone) ? apiCustomer.phone : (apiCustomer.phone ? [apiCustomer.phone] : []),
      address: (() => {
        try {
          const billing = formatAddress(apiCustomer.billingAddress);
          const shipping = formatAddress(apiCustomer.shippingAddress);
          const direct = formatAddress(apiCustomer.address);
          const finalAddress = billing || shipping || direct || '';

          // Ensure it's always a string, never an object
          if (typeof finalAddress === 'string') {
            return finalAddress.trim() || '';
          }
          // If it's an object, try to format it one more time
          if (typeof finalAddress === 'object' && finalAddress !== null) {
            const reformatted = formatAddress(finalAddress);
            return typeof reformatted === 'string' ? reformatted.trim() : '';
          }
          return '';
        } catch (err) {
          return '';
        }
      })(),
      profileImage: apiCustomer.profileImage || null,
      // Metrics are now fetched via separate KPIs API
      totalOrders: 0,
      totalValue: 0,
      totalTickets: 0,
      openTicketsCount: 0,
      resolvedTicketsCount: 0,
    }
    : null;

  return {
    customer: mappedCustomer,
    rawCustomer: apiCustomer, // Include raw customer data for detailed view
    error: null
  };
};

const Customer360 = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get customer ID from URL params
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("overview");
  const [errors, setErrors] = useState({});

  // Interactions tab state (similar to Lead 360)
  const [interactionsPage, setInteractionsPage] = useState(1);
  const [allInteractions, setAllInteractions] = useState([]);

  // Orders tab pagination state
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersLimit, setOrdersLimit] = useState(10);

  // Quotations tab pagination state
  const [quotationsPage, setQuotationsPage] = useState(1);
  const [quotationsLimit, setQuotationsLimit] = useState(10);

  // Selected interaction for detail modal
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [showInteractionModal, setShowInteractionModal] = useState(false);

  // Loading state for invoice downloads (track by quotationId)
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  // Message & Call modal states (reuse Lead 360 style)
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [messageType, setMessageType] = useState("whatsapp");
  const [messageContent, setMessageContent] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");
  const [callDirection, setCallDirection] = useState("outbound");
  const [callDuration, setCallDuration] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");

  // Redirect if no id
  useEffect(() => {
    if (!id) {
      navigate('/customer_360');
    }
  }, [id, navigate]);

  // Fetch customer 360 data with React Query
  const {
    data: customer360Data,
    isLoading,
    isError,
    error: customer360Error
  } = useQuery({
    queryKey: ['customer360', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomer360(id);
      return transformCustomer360Data(result, id);
    },
    enabled: !!id
  });

  // Fetch customer KPIs separately
  const {
    data: kpisData,
    isLoading: kpisLoading,
    isError: kpisError
  } = useQuery({
    queryKey: ['customerKpis', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerKpis(id);
      if (result.success) {
        return result.kpis;
      }
      throw new Error(result.error || 'Failed to fetch KPIs');
    },
    enabled: !!id
  });

  // Extract transformed data
  const customer = customer360Data?.customer || null;
  const rawCustomer = customer360Data?.rawCustomer || null;
  const error = customer360Data?.error || customer360Error?.message || null;

  // Use KPIs from separate API call, fallback to customer data if KPIs not loaded
  const kpis = kpisData || {
    totalOrders: customer?.totalOrders ?? 0,
    totalValue: customer?.totalValue ?? 0,
    totalTickets: customer?.totalTickets ?? 0,
    openTicketsCount: customer?.openTicketsCount ?? 0,
    resolvedTicketsCount: customer?.resolvedTicketsCount ?? 0
  };

  // Fetch interactions when Interactions tab is active (paginated)
  const {
    data: interactionsData,
    isLoading: interactionsLoading,
    isError: interactionsError,
    isFetching: interactionsLoadingMore
  } = useQuery({
    queryKey: ['customerInteractions', id, interactionsPage],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerInteractions(id, {
        sortBy: 'performedAt',
        sortOrder: 'desc',
        limit: 10,
        page: interactionsPage
      });

      if (result.success) {
        const newInteractions = result.activities || [];
        const pagination = result.pagination || {};
        const currentPage = pagination.currentPage || interactionsPage;
        const totalPages = pagination.totalPages || (newInteractions.length === 10 ? interactionsPage + 1 : interactionsPage);

        return {
          activities: newInteractions,
          currentPage,
          totalPages,
          hasMore: currentPage < totalPages
        };
      } else {
        throw new Error(result.error || 'Failed to fetch interactions');
      }
    },
    enabled: activeTab === 'interactions' && !!id,
    keepPreviousData: true
  });

  // Reset interactions when tab changes
  useEffect(() => {
    if (activeTab !== 'interactions') {
      // Clear state when leaving interactions tab
      setInteractionsPage(1);
      setAllInteractions([]);
    } else {
      // When switching to interactions tab, reset to page 1 and clear previous data
      setInteractionsPage(1);
      setAllInteractions([]);
    }
  }, [activeTab]);

  // Accumulate interactions across pages
  useEffect(() => {
    // Only process if we're on the interactions tab
    if (activeTab !== 'interactions') {
      return;
    }

    // Only accumulate if we have data and it matches the current page
    if (interactionsData?.activities) {
      const dataPage = interactionsData.currentPage || 1;

      // Only update if the data page matches the current interactions page
      if (dataPage === interactionsPage) {
        if (interactionsPage === 1) {
          // First page - replace all (don't append)
          setAllInteractions(interactionsData.activities);
        } else {
          // Subsequent pages - append (avoid duplicates)
          setAllInteractions(prev => {
            const existingIds = new Set(prev.map(a => a._id || a.activityId || a.id));
            const newActivities = interactionsData.activities.filter(
              a => !existingIds.has(a._id || a.activityId || a.id)
            );
            return [...prev, ...newActivities];
          });
        }
      }
    } else if (!interactionsLoading && !interactionsLoadingMore) {
      // If no data and not loading, clear interactions
      setAllInteractions([]);
    }
  }, [interactionsData, interactionsPage, activeTab, interactionsLoading, interactionsLoadingMore]);

  // Extract interactions data
  const interactions = allInteractions;
  const interactionsHasMore = interactionsData?.hasMore || false;

  const loadMoreInteractions = () => {
    if (interactionsHasMore && !interactionsLoading && !interactionsLoadingMore) {
      setInteractionsPage(prev => prev + 1);
    }
  };

  // Fetch orders when Orders tab is active (with pagination)
  const {
    data: ordersData,
    isLoading: ordersLoading,
    isError: ordersError
  } = useQuery({
    queryKey: ['customerOrders', id, ordersPage, ordersLimit],
    queryFn: async () => {
      if (!id) return null;
      const result = await orderService.getOrdersByCustomer(id, {
        page: ordersPage,
        limit: ordersLimit
      });

      if (result.success) {
        // Transform orders to match UI format
        // Backend returns: _id, orderNumber, status, createdAt, totalAmount, itemsCount, quotationId
        const mappedOrders = (result.data || []).map((order) => {
          return {
            id: order._id,
            displayId: order.orderNumber || order.orderId || order._id,
            date: order.createdAt || null,
            amount: order.totalAmount || 0,
            itemsCount: order.itemsCount || 0,
            quotationId: order.quotationId || null
          };
        });

        return {
          orders: mappedOrders,
          pagination: result.pagination || {}
        };
      } else {
        throw new Error(result.error || 'Failed to fetch orders');
      }
    },
    enabled: activeTab === 'orders' && !!id,
    keepPreviousData: true
  });

  // Extract orders data and pagination
  const recentOrders = ordersData?.orders || [];
  const ordersPagination = ordersData?.pagination || {};

  // Reset orders page when tab changes
  useEffect(() => {
    if (activeTab !== 'orders') {
      setOrdersPage(1);
    }
  }, [activeTab]);

  // Fetch quotations when Quotations tab is active (with pagination)
  const {
    data: quotationsData,
    isLoading: quotationsLoading,
    isError: quotationsError
  } = useQuery({
    queryKey: ['customerQuotations', id, quotationsPage, quotationsLimit],
    queryFn: async () => {
      if (!id) return null;
      const result = await quotationService.getQuotationsByCustomer(id, {
        page: quotationsPage,
        limit: quotationsLimit
      });

      if (result.success) {
        // Transform quotations to match UI format
        const mappedQuotations = (result.data || []).map((quotation) => {
          return {
            id: quotation._id,
            quoteNumber: quotation.quoteNumber || quotation._id,
            title: quotation.title || 'Untitled Quotation',
            status: quotation.status,
            date: quotation.createdAt || null,
            amount: quotation.totalAmount || 0,
            itemsCount: quotation.itemsCount || 0,
            isExpired: quotation.isExpired
          };
        });

        return {
          quotations: mappedQuotations,
          pagination: result.pagination || {}
        };
      } else {
        throw new Error(result.error || 'Failed to fetch quotations');
      }
    },
    enabled: activeTab === 'quotations' && !!id,
    keepPreviousData: true
  });

  // Extract quotations data and pagination
  const customerQuotations = quotationsData?.quotations || [];
  const quotationsPagination = quotationsData?.pagination || {};

  // Reset quotations page when tab changes
  useEffect(() => {
    if (activeTab !== 'quotations') {
      setQuotationsPage(1);
    }
  }, [activeTab]);

  // Helper functions for interactions display (similar to Lead 360)
  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return PhoneCall;
      case 'email': return MailIcon;
      case 'whatsapp': return MessageCircle;
      case 'meeting': return Calendar;
      case 'note': return FileText;
      default: return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call': return 'text-[#0F2942]';
      case 'email': return 'text-green-600';
      case 'whatsapp': return 'text-green-500';
      case 'meeting': return 'text-purple-600';
      case 'note': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getActivityBgColor = (type) => {
    switch (type) {
      case 'call': return 'bg-blue-100';
      case 'email': return 'bg-green-100';
      case 'whatsapp': return 'bg-green-100';
      case 'meeting': return 'bg-purple-100';
      case 'note': return 'bg-gray-100';
      default: return 'bg-gray-100';
    }
  };

  const getActivityTime = (activity) => {
    return activity.performedAt || activity.createdAt || new Date().toISOString();
  };

  const getPerformerName = (performedBy) => {
    if (!performedBy) return 'Unknown';
    if (typeof performedBy === 'string') return performedBy;
    if (performedBy.firstName && performedBy.lastName) {
      return `${performedBy.firstName} ${performedBy.lastName}`;
    }
    if (performedBy.firstName) return performedBy.firstName;
    if (performedBy.email) return performedBy.email;
    return 'Unknown';
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return null;
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else if (mins > 0) {
      if (secs === 0) {
        return `${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  };

  // Capitalize first letter of each word
  const capitalizeText = (text) => {
    if (!text) return '';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };


  // Handler Functions
  const handleMargERPView = () => {
    const margParams = new URLSearchParams({
      customer_id: customer.id,
      gstin: customer.gstin,
      action: "view_orders",
    });
    const margERPUrl = `https://margcompusoft.com/customer?${margParams}`;
    window.open(margERPUrl, "_blank");
  };

  const handleCreateQuote = () => {
    if (customer?.id) {
      navigate(`/crm/quotation-proposal/quote-builder?customerId=${customer.id}`);
    } else {
      navigate("/crm/quotation-proposal/dashboard");
    }
  };

  const handleDownloadInvoice = async (order) => {
    // Get quotationId from order
    const quoteId = order.quotationId;

    if (!quoteId) {
      alert('Invalid quotation ID. Cannot download invoice.');
      return;
    }

    try {
      setDownloadingInvoiceId(quoteId);
      const result = await quotationService.downloadInvoice(quoteId);

      if (!result.success) {
        alert(result.error || 'Failed to download invoice PDF. Please try again.');
      }
    } catch (error) {
      alert('Failed to download invoice PDF. Please try again.');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // **NEW: Message Handlers**
  const handleNewMessage = () => {
    setMessageRecipient(customer?.email || "");
    setShowMessageModal(true);
  };

  const handleSendMessage = () => {
    const requiredFields = ['messageContent'];
    if (messageType === "email") {
      requiredFields.push('messageSubject');
    }

    const formData = {
      messageContent,
      messageSubject: messageType === "email" ? messageSubject : ''
    };
    const validation = validateRequiredFields(formData, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      if (!messageContent.trim()) {
        alert("Please enter message content");
      } else if (messageType === "email" && !messageSubject.trim()) {
        alert("Please enter email subject");
      }
      return;
    }

    setErrors({});

    // Note: Interactions are now fetched via separate API
    // This is just for local UI feedback - actual interaction should be created via API
    const newInteraction = {
      id: Date.now(),
      type: messageType,
      message:
        messageType === "email"
          ? `Subject: ${messageSubject}\n${messageContent}`
          : messageContent,
      timestamp: new Date().toISOString(),
      status: "sent",
      direction: "outbound",
    };

    // Invalidate interactions query to refetch from API
    queryClient.invalidateQueries(['customerInteractions', id]);

    // Reset form
    setMessageContent("");
    setMessageSubject("");
    setMessageType("whatsapp");
    setShowMessageModal(false);

    alert(
      `${messageType.charAt(0).toUpperCase() + messageType.slice(1)} sent successfully!`
    );
  };

  // **NEW: Call Log Handlers**
  const handleNewCallLog = () => {
    setShowCallLogModal(true);
  };

  const handleSaveCallLog = () => {
    const requiredFields = ['callNotes'];
    const formData = { callNotes };
    const validation = validateRequiredFields(formData, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      alert("Please enter call notes");
      return;
    }

    setErrors({});

    // Note: Interactions are now fetched via separate API
    // This is just for local UI feedback - actual interaction should be created via API
    const newInteraction = {
      id: Date.now(),
      type: "call",
      message: `${callOutcome ? `Outcome: ${callOutcome} - ` : ""}${callNotes}`,
      timestamp: new Date().toISOString(),
      status: "completed",
      direction: callDirection,
      duration: callDuration || "00:00:00",
    };

    // Invalidate interactions query to refetch from API
    queryClient.invalidateQueries(['customerInteractions', id]);

    // Reset form
    setCallDirection("outbound");
    setCallDuration("");
    setCallNotes("");
    setCallOutcome("");
    setFollowUpRequired(false);
    setFollowUpDate("");
    setShowCallLogModal(false);

    alert("Call log saved successfully!");
  };

  const docTypes = [
    { value: "certificate", label: "Certificate" },
    { value: "license", label: "License" },
    { value: "identity", label: "Identity Document" },
    { value: "financial", label: "Financial Document" },
    { value: "other", label: "Other" },
  ];

  const messageTypes = [
    { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { value: "email", label: "Email", icon: MailIcon },
    { value: "sms", label: "SMS", icon: Phone },
  ];

  const callOutcomes = [
    { value: "successful", label: "Successful" },
    { value: "no_answer", label: "No Answer" },
    { value: "busy", label: "Busy" },
    { value: "voicemail", label: "Voicemail" },
    { value: "follow_up_required", label: "Follow-up Required" },
  ];

  const tabs = [
    { id: "overview", label: "Customer Info", icon: User },
    { id: "quotations", label: "Quotations", icon: FileText },
    { id: "orders", label: "Orders", icon: ShoppingCart },
    { id: "interactions", label: "Interactions", icon: MessageSquare },
    // { id: "tickets", label: "Tickets", icon: FileText },
    // { id: "documents", label: "Documents", icon: FileImage },
  ];

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case "low":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "low":
        return "bg-blue-100 text-blue-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading || kpisLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  // Graceful fallback if customer not loaded or error
  if (error || !customer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-700 font-medium mb-2">
            {error || 'Customer not found.'}
          </p>
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/customer_360')}
          >
            Back to Customer 360
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer 360°</h1>
              <p className="text-gray-600">Complete customer profile, orders, interactions, and support tickets</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <PermissionGate permission="customer_management" action="update">
              <button
                onClick={() => navigate(`/customer_360/edit/${id}`)}
                className="flex items-center px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033] transition-colors"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Customer
              </button>
            </PermissionGate>
            <PermissionGate permission="customer_management" action="create">
              <button
                onClick={() => navigate("/tickets_feedback")}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <FileText className="h-4 w-4 mr-2" />
                Create Ticket
              </button>
            </PermissionGate>
            <PermissionGate permission="quotation_management" action="create">
              <button
                onClick={handleCreateQuote}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Quote
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Key Metrics - Using separate KPIs API */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-[#0F2942]">
                Total Orders
              </p>
              <p className="text-xl font-bold text-blue-900 break-words leading-tight">
                {kpis.totalOrders ?? 0}
              </p>
            </div>
            <ShoppingCart className="h-8 w-8 text-[#0F2942] flex-shrink-0" />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-green-600">
                Total Value
              </p>
              <p
                className="text-xl font-bold text-green-900 break-words leading-tight"
                title={`₹${formatIndianAmount(kpis.totalValue ?? 0)}`}
              >
                ₹{formatIndianAmount(kpis.totalValue ?? 0)}
              </p>
            </div>
            <IndianRupee className="h-8 w-8 text-green-600 flex-shrink-0 font-bold text-2xl" />
          </div>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-indigo-600">
                Total Tickets
              </p>
              <p className="text-xl font-bold text-indigo-900 break-words leading-tight">
                {kpis.totalTickets ?? 0}
              </p>
            </div>
            <FileText className="h-8 w-8 text-indigo-600 flex-shrink-0" />
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-purple-600">
                Open Tickets
              </p>
              <p className="text-xl font-bold text-purple-900 break-words leading-tight">
                {kpis.openTicketsCount ?? 0}
              </p>
            </div>
            <Shield className="h-8 w-8 text-purple-600 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* Customer Info Header - Enhanced */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Profile Picture/Avatar */}
          <div className="flex-shrink-0">
            {customer.profileImage ? (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md relative group transition-transform hover:scale-105">
                <img
                  src={customer.profileImage}
                  alt={customer.name || 'Customer'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-agrosaf-green to-agrosaf-green-hover flex items-center justify-center text-white text-2xl font-bold avatar-fallback underline-none" style={{ display: 'none' }}>
                  {(customer.name || 'Customer')
                    .split(' ')
                    .filter(Boolean)
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              </div>
            ) : (
              <div className="w-24 h-24 bg-gradient-to-br from-agrosaf-green to-agrosaf-green-hover rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-md border-4 border-white transition-transform hover:scale-105">
                {(customer.name || 'Customer')
                  .split(' ')
                  .filter(Boolean)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
            )}
          </div>

          {/* Customer Details - Enhanced Layout */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1 capitalize">
                  {capitalizeText(customer.name)}
                </h2>
                {customer.businessName && (
                  <p className="text-base text-gray-600 capitalize flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {capitalizeText(customer.businessName)}
                  </p>
                )}
              </div>
              {/* Badges */}
              <div className="flex items-center gap-3">
                {/* Data Source Badge */}
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${rawCustomer?.isFromMarg
                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  : 'bg-teal-100 text-teal-800 border border-teal-200'
                  }`}>
                  {rawCustomer?.isFromMarg ? (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <Database className="h-4 w-4 mr-1.5 " />
                      Marg ERP
                    </div>
                  ) : (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <HardDrive className="h-4 w-4 mr-1.5 " />
                      CRM
                    </div>
                  )}
                </span>

                {/* Status Badge */}
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${rawCustomer?.isActive !== false
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                  {rawCustomer?.isActive !== false ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1.5" />
                      Active
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1.5" />
                      Blocked
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* Contact Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Email */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-[#0F2942]" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={customer.email}
                  >
                    {customer.email || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-green-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={Array.isArray(customer.phone) ? customer.phone.join(', ') : customer.phone}
                  >
                    {(Array.isArray(customer.phone) ? customer.phone[0] : customer.phone) || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Customer ID */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Tag className="h-5 w-5 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Customer ID</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={rawCustomer?.customerId}
                  >
                    {rawCustomer?.customerId || 'N/A'}
                  </p>
                </div>
              </div>

              {/* GST Number */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">GST Number</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={rawCustomer?.gstNumber}
                  >
                    {rawCustomer?.gstNumber || '--'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? "border-agrosaf-green text-agrosaf-green"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Customer Info Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {!rawCustomer ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No customer information available.</p>
                </div>
              ) : (
                <>
                  {/* Business Information Section */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
                      <PermissionGate permission="customer_management" action="update">
                        <Button
                          variant="outline"
                          size="sm"
                          icon={<Edit className="h-4 w-4" />}
                          onClick={() => navigate(`/customer_360/edit/${id}`)}
                        >
                          Edit Profile
                        </Button>
                      </PermissionGate>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Customer ID</p>
                        <p className="text-base font-medium text-gray-900">{rawCustomer.customerId || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                        <p className="text-base font-medium text-gray-900 capitalize">{rawCustomer.contactPerson || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Company Name</p>
                        <p className="text-base font-medium text-gray-900 capitalize break-all break-word">{rawCustomer.companyName || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Email</p>
                        <p className="text-base font-medium text-gray-900">{rawCustomer.email || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Phone</p>
                        <p className="text-base font-medium text-gray-900">
                          {(Array.isArray(rawCustomer.phone) ? rawCustomer.phone[0] : rawCustomer.phone) || '--'}
                        </p>
                      </div>
                      {Array.isArray(rawCustomer.phone) && rawCustomer.phone.slice(1).map((p, idx) => (
                        <div key={`alt-phone-${idx}`} className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Alternative Phone {idx + 1}</p>
                          <p className="text-base font-medium text-gray-900">{p}</p>
                        </div>
                      ))}

                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">GST Number</p>
                        <p className="text-base font-medium text-gray-900">{rawCustomer.gstNumber || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Drug License Number</p>
                        <p className="text-base font-medium text-gray-900">{rawCustomer.drugLicenseNumber || '--'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Company Profile information - Internal */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Profile</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Website</p>
                        {rawCustomer.website ? (
                          <a
                            href={rawCustomer.website.startsWith('http') ? rawCustomer.website : `https://${rawCustomer.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base font-medium text-[#0F2942] hover:text-[#0B2033] hover:underline break-all"
                          >
                            {rawCustomer.website}
                          </a>
                        ) : (
                          <p className="text-base font-medium text-gray-900">--</p>
                        )}
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Industry</p>
                        <p className="text-base font-medium text-gray-900 capitalize break-words">{rawCustomer.industry || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Company Size</p>
                        <p className="text-base font-medium text-gray-900 break-words">{rawCustomer.companySize || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Annual Revenue</p>
                        <p className="text-base font-medium text-gray-900">
                          {rawCustomer.annualRevenue ? `₹${Number(rawCustomer.annualRevenue).toLocaleString('en-IN')}` : '--'}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Region</p>
                        <p className="text-base font-medium text-gray-900 capitalize break-words">{rawCustomer.region || '--'}</p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Decision Maker</p>
                        <p className="text-base font-medium text-gray-900 break-words">{rawCustomer.decisionMaker || '--'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Marg ERP Details - NEW SECTION */}
                  {rawCustomer.isFromMarg && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Marg ERP Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Marg Customer ID (CID)</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margCID || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Marg Route</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margRoute || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Marg Area</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margArea || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">MR Code</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margMrCode || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Station Code (SCode)</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margSCode || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Credit Limit</p>
                          <p className="text-base font-medium text-gray-900">₹{formatIndianAmount(rawCustomer.creditLimit || 0)}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Credit Days</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditDays || 0} Days</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Credit Bills</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditBills || 0} Bills</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Credit Status</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditStatus || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Marg Rate Category</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margRate || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Marg Code</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margCode || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Additional field</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.margAddField || '--'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Addresses */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Addresses</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Billing Address */}
                      <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                            <MapPin className="h-4 w-4 text-[#0F2942]" />
                          </div>
                          <p className="text-base font-semibold text-gray-900">Billing Address</p>
                        </div>
                        {rawCustomer.billingAddress ? (
                          <div className="space-y-2">
                            {(() => {
                              const streetParts = [
                                rawCustomer.billingAddress.street,
                                rawCustomer.billingAddress.street2,
                                rawCustomer.billingAddress.street3
                              ].map(s => s?.trim()).filter(Boolean);

                              return streetParts.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Street</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize break-all break-word">{streetParts.join(', ')}</p>
                                </div>
                              );
                            })()}
                            <div className="grid grid-cols-2 gap-3">
                              {rawCustomer.billingAddress.city && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">City</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.billingAddress.city === 'object' ? rawCustomer.billingAddress.city?.name : rawCustomer.billingAddress.city}
                                  </p>
                                </div>
                              )}
                              {rawCustomer.billingAddress.state && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">State</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.billingAddress.state === 'object' ? rawCustomer.billingAddress.state?.name : rawCustomer.billingAddress.state}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {rawCustomer.billingAddress.pincode && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                                  <p className="text-sm font-medium text-gray-900">{rawCustomer.billingAddress.pincode}</p>
                                </div>
                              )}
                              {rawCustomer.billingAddress.country && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Country</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.billingAddress.country === 'object' ? rawCustomer.billingAddress.country?.name : rawCustomer.billingAddress.country}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Not provided</p>
                        )}
                      </div>

                      {/* Shipping Address */}
                      <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                        <div className="flex items-center gap-2 mb-4">
                          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                            <Truck className="h-4 w-4 text-green-600" />
                          </div>
                          <p className="text-base font-semibold text-gray-900">Shipping Address</p>
                        </div>
                        {rawCustomer.shippingAddress ? (
                          <div className="space-y-2">
                            {(() => {
                              const streetParts = [
                                rawCustomer.shippingAddress.street,
                                rawCustomer.shippingAddress.street2,
                                rawCustomer.shippingAddress.street3
                              ].map(s => s?.trim()).filter(Boolean);

                              return streetParts.length > 0 && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Street</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize break-all break-word">{streetParts.join(', ')}</p>
                                </div>
                              );
                            })()}
                            <div className="grid grid-cols-2 gap-3">
                              {rawCustomer.shippingAddress.city && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">City</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.shippingAddress.city === 'object' ? rawCustomer.shippingAddress.city?.name : rawCustomer.shippingAddress.city}
                                  </p>
                                </div>
                              )}
                              {rawCustomer.shippingAddress.state && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">State</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.shippingAddress.state === 'object' ? rawCustomer.shippingAddress.state?.name : rawCustomer.shippingAddress.state}
                                  </p>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              {rawCustomer.shippingAddress.pincode && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                                  <p className="text-sm font-medium text-gray-900">{rawCustomer.shippingAddress.pincode}</p>
                                </div>
                              )}
                              {rawCustomer.shippingAddress.country && (
                                <div>
                                  <p className="text-xs text-gray-500 mb-0.5">Country</p>
                                  <p className="text-sm font-medium text-gray-900 capitalize">
                                    {typeof rawCustomer.shippingAddress.country === 'object' ? rawCustomer.shippingAddress.country?.name : rawCustomer.shippingAddress.country}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Not provided</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Created At</p>
                        <p className="text-base font-medium text-gray-900">
                          {rawCustomer.createdAt ? formatDate(rawCustomer.createdAt, { includeTime: true }) : 'N/A'}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                        <p className="text-base font-medium text-gray-900">
                          {rawCustomer.updatedAt ? formatDate(rawCustomer.updatedAt, { includeTime: true }) : 'N/A'}
                        </p>
                      </div>
                      {Array.isArray(rawCustomer.tags) && rawCustomer.tags.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 md:col-span-2 lg:col-span-1">
                          <p className="text-sm text-gray-600 mb-2">Tags</p>
                          <div className="flex flex-wrap gap-2">
                            {rawCustomer.tags.map((tag, index) => (
                              <span
                                key={index}
                                className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Orders Tab */}
          {activeTab === "orders" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  All Orders
                </h3>
              </div>

              {ordersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading orders...</p>
                  </div>
                </div>
              ) : ordersError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Failed to load orders</p>
                  <p className="text-sm text-gray-500">{ordersError.message || 'An error occurred'}</p>
                </div>
              ) : recentOrders.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No recent orders available for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ordered On
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentOrders.map((order) => (
                        <tr key={order.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 uppercase">
                            {order.displayId}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.date ? formatDate(order.date, { includeTime: false }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ₹{formatIndianAmount(order.amount ?? 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.itemsCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {order.quotationId ? (
                              <div className="flex items-center space-x-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  icon={<Eye className="h-4 w-4" />}
                                  onClick={() => navigate(`/crm/quotation-proposal/send-track/${order.quotationId}?page=ordered`)}
                                  title="View Order"
                                />
                                <button
                                  type="button"
                                  onClick={() => handleDownloadInvoice(order)}
                                  className="p-2 text-[#0F2942] hover:text-[#0B2033] border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  disabled={downloadingInvoiceId === order.quotationId}
                                  title={downloadingInvoiceId === order.quotationId ? 'Downloading...' : 'Download Invoice'}
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs">N/A</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {(() => {
                const ordersTotalItems = Number(ordersPagination?.total || recentOrders.length || 0);
                const ordersTotalPages = Number(ordersPagination?.totalPages || 1);
                const effectivePaginationLimit = ordersPagination?.limit || ordersLimit || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !ordersLoading && ordersTotalPages > 1;
                const handleOrdersPageChange = (page) => {
                  setOrdersPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                };

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((ordersPagination.page || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((ordersPagination.page || 1) * effectivePaginationLimit, ordersTotalItems)} of {ordersTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={ordersLimit}
                            onChange={(e) => {
                              setOrdersLimit(Number(e.target.value));
                              setOrdersPage(1);
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleOrdersPageChange((ordersPagination.page || 1) - 1)}
                          disabled={(ordersPagination.page || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = ordersPagination.totalPages || 1;
                            const currentPageNum = ordersPagination.page || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handleOrdersPageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handleOrdersPageChange((ordersPagination.page || 1) + 1)}
                          disabled={(ordersPagination.page || 1) >= (ordersPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Quotations Tab */}
          {activeTab === "quotations" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  All Quotations
                </h3>
              </div>

              {quotationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading quotations...</p>
                  </div>
                </div>
              ) : quotationsError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Failed to load quotations</p>
                  <p className="text-sm text-gray-500">{quotationsError.message || 'An error occurred'}</p>
                </div>
              ) : customerQuotations.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No quotations available for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quote Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {customerQuotations.map((quotation) => (
                        <tr key={quotation.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {quotation.quoteNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.date ? formatDate(quotation.date, { includeTime: false }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ₹{formatIndianAmount(quotation.amount ?? 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.itemsCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className={`inline-flex items-center mr-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${['order', 'ordered'].includes(quotation.status?.toLowerCase())
                                ? getQuotationStatusClasses('accepted')
                                : getQuotationStatusClasses(quotation.status)
                                }`}>
                                {['order', 'ordered'].includes(quotation.status?.toLowerCase()) ? 'Accepted' : formatQuotationStatus(quotation.status)}
                              </span>
                              {quotation.status?.toLowerCase() === 'order' && (
                                <span className="text-[12px] text-gray-500 mt-1 ml-1 italic font-medium">(Ready to order)</span>
                              )}
                              {quotation.status?.toLowerCase() === 'ordered' && (
                                <span className="text-[12px] text-gray-500 mt-1 ml-1 italic font-medium">(Ordered)</span>
                              )}
                              {quotation.isExpired && (
                                <span className="text-[12px] text-red-600 mt-1 ml-1 italic font-medium">(Expired)</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="h-4 w-4" />}
                              onClick={() => navigate(`/crm/quotation-proposal/send-track/${quotation.id}?page=quote`)}
                              title="View Quotation"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {(() => {
                const quotationsTotalItems = Number(quotationsPagination?.total || customerQuotations.length || 0);
                const quotationsTotalPages = Number(quotationsPagination?.totalPages || 1);
                const effectivePaginationLimit = quotationsPagination?.limit || quotationsLimit || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !quotationsLoading && quotationsTotalPages > 1;
                const handleQuotationsPageChange = (page) => {
                  setQuotationsPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                };

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((quotationsPagination.page || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((quotationsPagination.page || 1) * effectivePaginationLimit, quotationsTotalItems)} of {quotationsTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={quotationsLimit}
                            onChange={(e) => {
                              setQuotationsLimit(Number(e.target.value));
                              setQuotationsPage(1);
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuotationsPageChange((quotationsPagination.page || 1) - 1)}
                          disabled={(quotationsPagination.page || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = quotationsPagination.totalPages || 1;
                            const currentPageNum = quotationsPagination.page || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handleQuotationsPageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handleQuotationsPageChange((quotationsPagination.page || 1) + 1)}
                          disabled={(quotationsPagination.page || 1) >= (quotationsPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Interactions Tab - Timeline format (similar to Lead 360) */}
          {activeTab === "interactions" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Activity Timeline</h3>
              </div>

              {interactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading interactions...</p>
                  </div>
                </div>
              ) : interactionsError ? (
                <div className="text-center py-12">
                  <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 mb-2">Failed to load interactions</p>
                  <p className="text-sm text-gray-500">{interactionsError.message || 'An error occurred'}</p>
                </div>
              ) : interactions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No interactions logged yet</p>
                  <p className="text-sm text-gray-500 mt-2">Interactions will appear here once logged</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-6">
                    {interactions.map((activity) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const iconColor = getActivityColor(activity.type);
                      const bgColor = getActivityBgColor(activity.type);

                      return (
                        <div
                          key={activity._id || activity.activityId}
                          className="relative pl-12 cursor-pointer"
                          onClick={() => {
                            const activityId = activity._id || activity.activityId || activity.id;
                            if (activityId) {
                              navigate(`/activity_detail/${activityId}`);
                            }
                          }}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center border-2 border-white shadow-sm`}>
                            <ActivityIcon className={`h-4 w-4 ${iconColor}`} />
                          </div>

                          {/* Activity card */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className={`text-base sm:text-lg font-semibold text-gray-900 ${activity.type === 'email' ? 'truncate' : ''
                                    }`}>
                                    {capitalizeText(activity.title || '')}
                                  </h4>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.type === 'call' ? 'bg-blue-100 text-blue-800' :
                                    activity.type === 'email' ? 'bg-green-100 text-green-800' :
                                      activity.type === 'whatsapp' ? 'bg-green-100 text-green-800' :
                                        activity.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                    }`}>
                                    {capitalizeText(activity.type || '')}
                                  </span>
                                  {activity.isAutomated && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Auto
                                    </span>
                                  )}
                                </div>

                                {activity.description && (
                                  <p className={`text-sm text-gray-600 mb-3 break-words ${activity.type === 'email' ? 'line-clamp-2' : 'whitespace-pre-wrap'
                                    }`}>
                                    {activity.description}
                                  </p>
                                )}

                                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  <div className="flex items-center gap-1">
                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <span>{formatDate(getActivityTime(activity), { includeTime: true })}</span>
                                  </div>

                                  {activity.callMetadata?.talkDuration && activity.callMetadata.talkDuration > 0 && (
                                    <div className="flex items-center gap-1">
                                      <span>Duration: {formatDuration(activity.callMetadata.talkDuration)}</span>
                                    </div>
                                  )}

                                  {activity.performedBy && (
                                    <div className="flex items-center gap-1">
                                      <User className="h-3 w-3 sm:h-4 sm:w-4" />
                                      <span>{getPerformerName(activity.performedBy)}</span>
                                    </div>
                                  )}

                                  {activity.outcome && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.outcome === 'completed' ? 'bg-green-100 text-green-800' :
                                      activity.outcome === 'sent' ? 'bg-green-100 text-green-800' :
                                        activity.outcome === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                          activity.outcome === 'cancelled' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                      }`}>
                                      {capitalizeText(activity.outcome)}
                                    </span>
                                  )}
                                </div>

                                {activity.scheduledFor && (
                                  <div className="mt-3 pt-3 border-t border-gray-200">
                                    <div className="flex items-center gap-2 text-sm text-[#0F2942]">
                                      <Calendar className="h-4 w-4" />
                                      <span>Scheduled for: {formatDate(activity.scheduledFor, { includeTime: true })}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {interactionsHasMore && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMoreInteractions}
                          disabled={interactionsLoadingMore}
                        >
                          {interactionsLoadingMore ? 'Loading...' : 'Load More'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Documents Tab (removed as per requirements) */}
        </div>
      </div>

      {/* **NEW: Message Modal** */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                New Message
              </h3>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Type
                </label>
                <div className="flex space-x-2">
                  {messageTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setMessageType(type.value)}
                        className={`flex items-center px-3 py-2 rounded-lg border ${messageType === type.value
                          ? "bg-blue-50 border-blue-300 text-[#0B2033]"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient
                </label>
                <input
                  type="text"
                  value={messageRecipient}
                  onChange={(e) => {
                    const sanitized = handleTextInputChange(e, null, 100);
                    setMessageRecipient(sanitized);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  placeholder={
                    messageType === "email" ? "Email address" : "Phone number"
                  }
                  maxLength={100}
                />
              </div>

              {messageType === "email" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => {
                        const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.title);
                        setMessageSubject(sanitized);
                        if (errors.messageSubject) {
                          setErrors(prev => ({ ...prev, messageSubject: '' }));
                        }
                      }}
                      className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${errors.messageSubject ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Email subject"
                      maxLength={FIELD_LIMITS.title}
                      required
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      {getCharacterCount(messageSubject, FIELD_LIMITS.title)}
                    </div>
                  </div>
                  {errors.messageSubject && (
                    <p className="text-xs text-red-600 mt-1">{errors.messageSubject}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <div className="relative">
                  <textarea
                    value={messageContent}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.message);
                      setMessageContent(sanitized);
                      if (errors.messageContent) {
                        setErrors(prev => ({ ...prev, messageContent: '' }));
                      }
                    }}
                    rows={4}
                    className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${errors.messageContent ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter your message..."
                    maxLength={FIELD_LIMITS.message}
                    required
                  />
                  <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                    {getCharacterCount(messageContent, FIELD_LIMITS.message)}
                  </div>
                </div>
                {errors.messageContent && (
                  <p className="text-xs text-red-600 mt-1">{errors.messageContent}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageContent("");
                  setMessageSubject("");
                  setMessageType("whatsapp");
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                className="flex items-center px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033]"
              >
                <Send className="h-4 w-4 mr-2" />
                Send
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interaction Detail Modal */}
      {showInteractionModal && selectedInteraction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Interaction Details
              </h3>
              <button
                onClick={() => {
                  setShowInteractionModal(false);
                  setSelectedInteraction(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  {(() => {
                    const Icon = getActivityIcon(selectedInteraction.type);
                    const color = getActivityColor(selectedInteraction.type);
                    return <Icon className={`h-5 w-5 ${color}`} />;
                  })()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {capitalizeText(selectedInteraction.title || selectedInteraction.type || 'Interaction')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(getActivityTime(selectedInteraction), { includeTime: true })}
                  </p>
                </div>
              </div>

              {selectedInteraction.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Description</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {selectedInteraction.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Type</h4>
                  <p className="text-sm text-gray-900 capitalize">
                    {capitalizeText(selectedInteraction.type || 'N/A')}
                  </p>
                </div>
                {selectedInteraction.outcome && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Outcome</h4>
                    <p className="text-sm text-gray-900 capitalize">
                      {capitalizeText(selectedInteraction.outcome)}
                    </p>
                  </div>
                )}
                {selectedInteraction.performedBy && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Performed By</h4>
                    <p className="text-sm text-gray-900">
                      {getPerformerName(selectedInteraction.performedBy)}
                    </p>
                  </div>
                )}
                {selectedInteraction.channel && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Channel</h4>
                    <p className="text-sm text-gray-900 capitalize">
                      {capitalizeText(selectedInteraction.channel)}
                    </p>
                  </div>
                )}
              </div>

              {/* Call-specific metadata */}
              {selectedInteraction.type === 'call' && selectedInteraction.callMetadata && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Call Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                    {selectedInteraction.callMetadata.status && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                          Status
                        </span>
                        <span className="capitalize">
                          {selectedInteraction.callMetadata.status}
                        </span>
                      </div>
                    )}
                    {selectedInteraction.callMetadata.talkDuration &&
                      selectedInteraction.callMetadata.talkDuration > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                            Talk Time
                          </span>
                          <span>
                            {formatDuration(selectedInteraction.callMetadata.talkDuration)}
                          </span>
                        </div>
                      )}
                    {selectedInteraction.callMetadata.startTime && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                          Started At
                        </span>
                        <span>
                          {formatDate(selectedInteraction.callMetadata.startTime, { includeTime: true })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* **NEW: Call Log Modal** */}
      {showCallLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Log Call</h3>
              <button
                onClick={() => {
                  setShowCallLogModal(false);
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Direction
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCallDirection("outbound")}
                    className={`flex items-center px-3 py-2 rounded-lg border ${callDirection === "outbound"
                      ? "bg-blue-50 border-blue-300 text-[#0B2033]"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Outbound Call
                  </button>
                  <button
                    onClick={() => setCallDirection("inbound")}
                    className={`flex items-center px-3 py-2 rounded-lg border ${callDirection === "inbound"
                      ? "bg-blue-50 border-blue-300 text-[#0B2033]"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Inbound Call
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (HH:MM:SS)
                  </label>
                  <input
                    type="text"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                    placeholder="00:05:30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Outcome
                  </label>
                  <select
                    value={callOutcome}
                    onChange={(e) => setCallOutcome(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  >
                    <option value="">Select outcome</option>
                    {callOutcomes.map((outcome) => (
                      <option key={outcome.value} value={outcome.value}>
                        {outcome.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Notes *
                </label>
                <div className="relative">
                  <textarea
                    value={callNotes}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                      setCallNotes(sanitized);
                      if (errors.callNotes) {
                        setErrors(prev => ({ ...prev, callNotes: '' }));
                      }
                    }}
                    rows={4}
                    className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50 ${errors.callNotes ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter call notes and summary..."
                    maxLength={FIELD_LIMITS.notes}
                    required
                  />
                  <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                    {getCharacterCount(callNotes, FIELD_LIMITS.notes)}
                  </div>
                </div>
                {errors.callNotes && (
                  <p className="text-xs text-red-600 mt-1">{errors.callNotes}</p>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="followUp"
                  checked={followUpRequired}
                  onChange={(e) => setFollowUpRequired(e.target.checked)}
                  className="h-4 w-4 text-[#0F2942] focus:ring-[#0F2942]/50 border-gray-300 rounded"
                />
                <label
                  htmlFor="followUp"
                  className="text-sm font-medium text-gray-700"
                >
                  Follow-up required
                </label>
              </div>

              {followUpRequired && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Date
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F2942]/50"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCallLogModal(false);
                  setCallDirection("outbound");
                  setCallDuration("");
                  setCallNotes("");
                  setCallOutcome("");
                  setFollowUpRequired(false);
                  setFollowUpDate("");
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCallLog}
                className="flex items-center px-4 py-2 bg-[#0F2942] text-white rounded-lg hover:bg-[#0B2033]"
              >
                <PhoneCall className="h-4 w-4 mr-2" />
                Save Call Log
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Customer360;

